/* ==========================================================================
   Marhaba Sooq — custom storefront behaviour
   Defines two lightweight web components: <msq-sticky-atc> and
   <msq-countdown>. Loaded deferred so it never blocks LCP.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- Sticky mobile Buy Now / COD bar ---------------------------------- */
  // Shows a fixed bottom bar once the real add-to-cart button scrolls out of
  // view. Tapping it delegates to Dawn's existing add-to-cart button so all
  // cart/variant logic is reused.
  class MsqStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.msq-sticky-atc__btn');
      const targetId = this.button && this.button.getAttribute('data-target');
      this.target = targetId ? document.getElementById(targetId) : null;

      if (!this.button || !this.target) return;

      document.body.classList.add('msq-has-sticky-atc');

      this.button.addEventListener('click', () => {
        // Reuse Dawn's add-to-cart flow (opens cart drawer / redirects).
        this.target.click();
      });

      // Keep the sticky label/availability in sync with the real button.
      this.syncState();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Show the bar only while the main CTA is off-screen.
              this.classList.toggle('msq-is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -40px 0px', threshold: 0 }
        );
        this.observer.observe(this.target);
      } else {
        this.classList.add('msq-is-visible');
      }
    }

    syncState() {
      const disabled = this.target.hasAttribute('disabled');
      this.button.disabled = disabled;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('msq-has-sticky-atc');
    }
  }

  if (!customElements.get('msq-sticky-atc')) {
    customElements.define('msq-sticky-atc', MsqStickyAtc);
  }

  /* ---- Estimated-delivery countdown ------------------------------------- */
  // Counts down to a configurable daily cutoff (Asia/Dubai). After the cutoff
  // it rolls to the next day and swaps the message to the after-cutoff copy.
  class MsqCountdown extends HTMLElement {
    connectedCallback() {
      this.timerEl = this.querySelector('.msq-countdown__timer');
      this.beforeEl = this.querySelector('[data-before]');
      this.afterEl = this.querySelector('[data-after]');
      this.cutoffHour = parseInt(this.getAttribute('data-cutoff-hour'), 10);
      if (Number.isNaN(this.cutoffHour)) this.cutoffHour = 18;
      // Fixed UTC offset for the UAE (Asia/Dubai, UTC+4, no DST).
      this.tzOffsetMinutes = 4 * 60;

      if (!this.timerEl) return;
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    // Current time expressed in UAE wall-clock, as a Date built from UTC parts.
    nowInTz() {
      const now = new Date();
      return new Date(now.getTime() + this.tzOffsetMinutes * 60000);
    }

    tick() {
      const now = this.nowInTz();
      const cutoff = new Date(now.getTime());
      cutoff.setUTCHours(this.cutoffHour, 0, 0, 0);

      const beforeCutoff = now.getTime() < cutoff.getTime();
      if (this.beforeEl && this.afterEl) {
        this.beforeEl.hidden = !beforeCutoff;
        this.afterEl.hidden = beforeCutoff;
      }

      let remaining;
      if (beforeCutoff) {
        remaining = cutoff.getTime() - now.getTime();
      } else {
        // Count down to the next day's cutoff.
        const next = new Date(cutoff.getTime() + 24 * 3600 * 1000);
        remaining = next.getTime() - now.getTime();
      }

      this.timerEl.textContent = this.format(remaining);
    }

    format(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));
      const h = String(Math.floor(total / 3600)).padStart(2, '0');
      const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
      const s = String(total % 60).padStart(2, '0');
      return h + ':' + m + ':' + s;
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }
  }

  if (!customElements.get('msq-countdown')) {
    customElements.define('msq-countdown', MsqCountdown);
  }

  /* ---- Social proof: "people viewing right now" ------------------------- */
  // Seeds a plausible live viewer count and jitters it every few seconds so
  // the product feels in-demand. Purely cosmetic / dummy data.
  function initSocialProof() {
    document.querySelectorAll('.msq-socialproof').forEach(function (el) {
      var viewingEl = el.querySelector('.msq-sp__viewing');
      if (!viewingEl) return;
      var min = parseInt(el.getAttribute('data-view-min'), 10) || 5;
      var max = parseInt(el.getAttribute('data-view-max'), 10) || 25;
      if (max < min) max = min;
      function rand() { return Math.floor(Math.random() * (max - min + 1)) + min; }
      var current = rand();
      viewingEl.textContent = current;
      setInterval(function () {
        // drift by ±2, stay within bounds — feels organic, not random noise
        current += Math.floor(Math.random() * 5) - 2;
        if (current < min) current = min;
        if (current > max) current = max;
        viewingEl.textContent = current;
      }, 4000 + Math.floor(Math.random() * 3000));
    });
  }
  if (document.readyState !== 'loading') initSocialProof();
  else document.addEventListener('DOMContentLoaded', initSocialProof);

  /* ---- Product tabs ----------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('[data-msq-tabs]').forEach(function (root) {
      var btns = root.querySelectorAll('.msq-tabs__btn');
      var panels = root.querySelectorAll('.msq-tabs__panel');
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id = btn.getAttribute('data-tab');
          btns.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
          panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-panel') === id); });
        });
      });
    });
  }
  if (document.readyState !== 'loading') initTabs();
  else document.addEventListener('DOMContentLoaded', initTabs);

  /* ---- Hero slider ------------------------------------------------------ */
  function initHeroSlider() {
    document.querySelectorAll('[data-msq-heroslider]').forEach(function (s) {
      var track = s.querySelector('.msq-heroslider__track');
      var slides = s.querySelectorAll('.msq-heroslider__slide');
      var dots = s.querySelectorAll('.msq-heroslider__dot');
      var prev = s.querySelector('.msq-heroslider__nav--prev');
      var next = s.querySelector('.msq-heroslider__nav--next');
      if (!track || slides.length < 2) return;
      var idx = 0;
      function update() { dots.forEach(function (d, di) { d.classList.toggle('is-active', di === idx); }); }
      function go(i) { idx = (i + slides.length) % slides.length; track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' }); update(); }
      if (prev) prev.addEventListener('click', function () { go(idx - 1); });
      if (next) next.addEventListener('click', function () { go(idx + 1); });
      dots.forEach(function (d) { d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-index'), 10)); }); });
      var st;
      track.addEventListener('scroll', function () {
        clearTimeout(st);
        st = setTimeout(function () { idx = Math.round(track.scrollLeft / track.clientWidth); update(); }, 120);
      });
      if (s.getAttribute('data-autoplay') === 'true') {
        setInterval(function () { go(idx + 1); }, 6000);
      }
    });
  }
  if (document.readyState !== 'loading') initHeroSlider();
  else document.addEventListener('DOMContentLoaded', initHeroSlider);

  /* ---- Product carousel (page by one screen of 4) ----------------------- */
  function initProductCarousel() {
    document.querySelectorAll('[data-msq-pcarousel]').forEach(function (c) {
      var track = c.querySelector('.msq-pcarousel__track');
      var prev = c.querySelector('.msq-pcarousel__nav--prev');
      var next = c.querySelector('.msq-pcarousel__nav--next');
      if (!track) return;
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.hidden = track.scrollLeft <= 2;
        if (next) next.hidden = track.scrollLeft >= max || max <= 0;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(update); });
      window.addEventListener('resize', update);
      update();
    });
  }
  if (document.readyState !== 'loading') initProductCarousel();
  else document.addEventListener('DOMContentLoaded', initProductCarousel);

  /* ---- Wishlist heart on product cards (localStorage, no login/app) ------ */
  // Injects a heart button into every product card and persists liked product
  // handles in localStorage. Reveals on hover (desktop) via CSS; stays visible
  // once active. Purely client-side — no account or paid app required.
  function initWishlist() {
    var KEY = 'msq_wishlist';
    var store;
    try { store = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { store = []; }
    if (!Array.isArray(store)) store = [];
    var heartSvg = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7.6-4.7-10.1-9.3C.1 8.2 1.9 4.8 5.4 4.8c2 0 3.4 1.1 3.9 2.5C9.8 5.9 11.2 4.8 13.2 4.8c3.5 0 5.3 3.4 3.5 6.9C14.2 16.3 12 21 12 21z"/></svg>';
    var cartSvg = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM7.2 14.6l.9-1.6h7.4c.7 0 1.3-.4 1.7-1l3.6-6.5-1.7-1-3.6 6.5H8.5L4.3 2H1v2h2l3.6 7.6-1.4 2.5c-.7 1.3.2 2.9 1.7 2.9h12v-2H7.2z"/></svg>';
    document.querySelectorAll('.card-wrapper').forEach(function (card) {
      if (card.querySelector('.msq-wish')) return;
      var link = card.querySelector('a[href*="/products/"]');
      var handle = '';
      if (link) {
        var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        if (m) handle = m[1];
      }
      if (!handle) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'msq-wish';
      btn.setAttribute('aria-label', 'Add to wishlist');
      btn.setAttribute('aria-pressed', store.indexOf(handle) !== -1 ? 'true' : 'false');
      btn.innerHTML = heartSvg;
      if (store.indexOf(handle) !== -1) btn.classList.add('is-active');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var i = store.indexOf(handle);
        if (i === -1) { store.push(handle); btn.classList.add('is-active'); btn.setAttribute('aria-pressed', 'true'); }
        else { store.splice(i, 1); btn.classList.remove('is-active'); btn.setAttribute('aria-pressed', 'false'); }
        try { localStorage.setItem(KEY, JSON.stringify(store)); } catch (e2) {}
      });
      card.appendChild(btn);

      // Compact add-to-cart icon that triggers the (collapsed) real quick-add.
      var qbtn = card.querySelector('.quick-add__submit');
      if (qbtn) {
        var cbtn = document.createElement('button');
        cbtn.type = 'button';
        cbtn.className = 'msq-cart';
        cbtn.setAttribute('aria-label', 'Add to cart');
        cbtn.innerHTML = cartSvg;
        cbtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          qbtn.click();
          cbtn.classList.add('is-added');
          setTimeout(function () { cbtn.classList.remove('is-added'); }, 1200);
        });
        card.appendChild(cbtn);
      }
    });
  }
  if (document.readyState !== 'loading') initWishlist();
  else document.addEventListener('DOMContentLoaded', initWishlist);

  /* ---- PDP: hover-zoom (magnifier) on the main product image ------------- */
  // Desktop only. Magnifies the active image, tracking the cursor. Click still
  // opens Dawn's full lightbox — the two behaviours coexist.
  function initImageZoom() {
    if (!window.matchMedia || !window.matchMedia('(min-width: 990px)').matches) return;
    document.querySelectorAll('.product__media-item').forEach(function (item) {
      var img = item.querySelector('img');
      if (!img || item.classList.contains('msq-zoomable')) return;
      item.classList.add('msq-zoomable');
      item.addEventListener('mousemove', function (e) {
        var r = item.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      item.addEventListener('mouseenter', function () { item.classList.add('msq-zooming'); });
      item.addEventListener('mouseleave', function () {
        item.classList.remove('msq-zooming');
        img.style.transformOrigin = 'center center';
      });
    });
  }
  if (document.readyState !== 'loading') initImageZoom();
  else document.addEventListener('DOMContentLoaded', initImageZoom);

  /* ---- Header "LIVE CHAT" -> open Shopify Inbox chat --------------------- */
  // Shopify Inbox has no official "open" API, so we click its own launcher
  // button. Inbox loads async, so we retry briefly if it isn't ready yet.
  function initLiveChat() {
    var triggers = document.querySelectorAll('[data-msq-livechat]');
    if (!triggers.length) return;
    var SELECTORS = [
      '#shopify-chat button',
      'button#shopify-chat',
      '[id^="shopify-chat"] button',
      'shopify-chat button',
      '#ShopifyChat button',
      'button[aria-label="Chat window"]',
      'button[aria-label*="chat" i]',
      '.shopify-chat-button'
    ];
    function findLauncher() {
      for (var i = 0; i < SELECTORS.length; i++) {
        var el = document.querySelector(SELECTORS[i]);
        if (el) return el;
      }
      return null;
    }
    function openChat() {
      var el = findLauncher();
      if (el) { el.click(); return true; }
      return false;
    }
    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        if (openChat()) return;
        var tries = 0;
        var iv = setInterval(function () {
          tries += 1;
          if (openChat() || tries > 12) clearInterval(iv);
        }, 300);
      });
    });
  }
  if (document.readyState !== 'loading') initLiveChat();
  else document.addEventListener('DOMContentLoaded', initLiveChat);
})();
